import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { ApiResponse } from '@magnetar/admin-api-interfaces'
import { AppointmentSetting } from '@/hooks/useAppointmentSetting'

export type AppointmentMode = 'DEFAULT' | 'SHARED_ROOMS'

export interface CreateAppointmentSettingData {
  clinicId?: number
  examinationRoomId?: number       // 舊欄位，向後相容（單一診間）
  examinationRoomIds?: number[]    // 新欄位（多診間）
  doctorId?: number
  appointmentMode?: AppointmentMode
  appointmentType: 'SEQUENTIAL' | 'TIME_BASED'
  metadata: Record<string, unknown>
  dayStartTime?: string
  dayEndTime?: string
  maxAppointments?: number
}

export interface UpdateAppointmentSettingData {
  appointmentType?: 'SEQUENTIAL' | 'TIME_BASED'
  appointmentMode?: AppointmentMode  // 帶這個會觸發後端的模式切換流程
  metadata?: Record<string, unknown>
  dayStartTime?: string
  dayEndTime?: string
  maxAppointments?: number
  isActive?: boolean
}

export interface Appointment {
  appointment_id: number
  clinic_id: number
  examination_room_id?: number
  doctor_id?: number
  appointment_setting_id: number
  appointment_date: string
  appointment_time?: string
  sequence_number?: number
  patient_name: string
  patient_health_id?: string
  patient_phone?: string
  patient_email?: string
  appointment_source: 'ONLINE' | 'WALK_IN' | 'PHONE'
  appointment_type: 'SEQUENTIAL' | 'TIME_BASED'
  appointment_status:
    | 'SCHEDULED'
    | 'CHECKED_IN'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'NO_SHOW'
  notes?: string
  metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
  deleted_at?: string
}

export interface CreateAppointmentData {
  clinicId: number
  examinationRoomId?: number
  doctorId?: number
  appointmentSettingId: number
  appointmentDate: string
  appointmentTime?: string
  sequenceNumber?: number
  patientName: string
  patientHealthId?: string
  patientPhone?: string
  patientEmail?: string
  appointmentSource: 'ONLINE' | 'WALK_IN' | 'PHONE'
  notes?: string
  metadata?: Record<string, unknown>
}

export interface UpdateAppointmentData {
  examinationRoomId?: number
  doctorId?: number
  appointmentSettingId?: number
  appointmentDate?: string
  appointmentTime?: string
  sequenceNumber?: number
  patientName?: string
  patientHealthId?: string
  patientPhone?: string
  patientEmail?: string
  appointmentSource?: 'ONLINE' | 'WALK_IN' | 'PHONE'
  appointmentStatus?:
    | 'SCHEDULED'
    | 'CHECKED_IN'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'NO_SHOW'
  notes?: string
  metadata?: Record<string, unknown>
}

export const appointmentService = {
  // 建立預約掛號設定
  createAppointmentSetting: async (data: CreateAppointmentSettingData, token: string) => {
    try {
      const response = await apiClient.post<ApiResponse<AppointmentSetting>>(
        '/api/appointment-setting',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'CREATE_APPOINTMENT_SETTING_FAILED',
          message: '建立預約掛號設定失敗',
        },
      }
    }
  },

  // 更新預約掛號設定
  updateAppointmentSetting: async (
    appointmentSettingId: number,
    data: UpdateAppointmentSettingData,
    token: string,
  ) => {
    try {
      const response = await apiClient.put<ApiResponse<AppointmentSetting>>(
        `/api/appointment-setting/${appointmentSettingId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'UPDATE_APPOINTMENT_SETTING_FAILED',
          message: '更新預約掛號設定失敗',
        },
      }
    }
  },

  // 建立預約
  createAppointment: async (data: CreateAppointmentData, token: string) => {
    try {
      const response = await apiClient.post<ApiResponse<Appointment>>('/api/appointments', data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'CREATE_APPOINTMENT_FAILED',
          message: '建立預約失敗',
        },
      }
    }
  },

  // 更新預約
  updateAppointment: async (appointmentId: number, data: UpdateAppointmentData, token: string) => {
    try {
      const response = await apiClient.put<ApiResponse<Appointment>>(
        `/api/appointment/${appointmentId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'UPDATE_APPOINTMENT_FAILED',
          message: '更新預約失敗',
        },
      }
    }
  },

  // 刪除預約
  deleteAppointment: async (appointmentId: number, token: string) => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/appointment/${appointmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'DELETE_APPOINTMENT_FAILED',
          message: '刪除預約失敗',
        },
      }
    }
  },

  // 派發預約到指定診間（SHARED_ROOMS 模式）
  dispatchAppointment: async (
    appointmentId: number,
    examinationRoomId: number,
    token: string,
  ) => {
    try {
      const response = await apiClient.patch<ApiResponse<Appointment>>(
        `/api/appointment/${appointmentId}/dispatch`,
        { examinationRoomId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: { code: 'DISPATCH_APPOINTMENT_FAILED', message: '派發預約失敗' },
      }
    }
  },

  // 切換預約模式（DEFAULT <-> SHARED_ROOMS）
  switchMode: async (appointmentSettingId: number, targetMode: AppointmentMode, token: string) => {
    try {
      const response = await apiClient.put<ApiResponse<AppointmentSetting>>(
        `/api/appointment-setting/${appointmentSettingId}`,
        { appointmentMode: targetMode },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: { code: 'SWITCH_MODE_FAILED', message: '切換預約模式失敗' },
      }
    }
  },

  // 取得診所下所有預約掛號設定（共用模式查詢用）
  getAppointmentSettingsByClinic: async (clinicId: number, token: string) => {
    try {
      const response = await apiClient.get<ApiResponse<AppointmentSetting[]>>(
        `/api/appointment-setting/by-clinic/${clinicId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: { code: 'FETCH_CLINIC_SETTINGS_FAILED', message: '取得診所預約設定失敗' },
      }
    }
  },

  // 設定預約為完成
  updateAppointmentToCompleted: async (appointmentId: number, token: string) => {
    try {
      const response = await apiClient.put<ApiResponse<Appointment>>(
        `/api/appointment/${appointmentId}/completed`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: {
          code: 'UPDATE_APPOINTMENT_FAILED',
          message: '更新預約失敗',
        },
      }
    }
  },
}
